import React, { useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentFormErrors } from '../types/payment-form.types';
import type { CreditCardData } from '../../../../types';

interface StripeCardElementsFormProps {
  creditCardData: CreditCardData;
  errors: PaymentFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  stripePublishableKey: string;
  onCardElementReady?: (handlers: { getCardElement: () => any; getStripe: () => unknown }) => void;
}

const cardElementStyle = {
  base: {
    fontSize: '16px',
    color: '#1f2937',
    '::placeholder': { color: '#9ca3af' },
  },
  invalid: {
    color: '#ef4444',
  },
};

const InnerStripeForm: React.FC<Omit<StripeCardElementsFormProps, 'stripePublishableKey'> & { onCardElementReady?: StripeCardElementsFormProps['onCardElementReady'] }> = ({
  creditCardData,
  errors,
  onChange,
  onCardElementReady,
}) => {
  const elements = useElements();
  const stripe = useStripe();

  React.useEffect(() => {
    if (onCardElementReady && elements && stripe) {
      onCardElementReady({
        getCardElement: () => (elements as any).getElement(CardNumberElement),
        getStripe: () => stripe,
      });
    }
  }, [elements, stripe, onCardElementReady]);

  const errorBorderClass = 'border-red-500';
  const normalBorderClass = 'border-medium-gray dark:border-gray-600';

  return (
    <div className="space-y-4">
      {/* Número do cartão */}
      <div>
        <label className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
          Número do cartão
        </label>
        <div className={`p-3 border rounded-md shadow-sm ${errors.cardNumber ? errorBorderClass : normalBorderClass}`}>
          <CardNumberElement options={{ style: cardElementStyle, disableLink: true }} />
        </div>
        {errors.cardNumber && <p className="text-xs text-red-500 mt-1">{errors.cardNumber}</p>}
      </div>

      {/* Validade e CVV lado a lado */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
            Validade
          </label>
          <div className={`p-3 border rounded-md shadow-sm ${errors.cardNumber ? errorBorderClass : normalBorderClass}`}>
            <CardExpiryElement options={{ style: cardElementStyle }} />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
            CVV
          </label>
          <div className={`p-3 border rounded-md shadow-sm ${errors.cardNumber ? errorBorderClass : normalBorderClass}`}>
            <CardCvcElement options={{ style: cardElementStyle }} />
          </div>
        </div>
      </div>
    </div>
  );
};

const StripeCardElementsForm: React.FC<StripeCardElementsFormProps> = (props) => {
  const { stripePublishableKey, onCardElementReady, ...innerProps } = props;
  const stripePromise = useMemo(() => loadStripe(stripePublishableKey), [stripePublishableKey]);

  if (!stripePublishableKey) return null;

  return (
    <Elements stripe={stripePromise}>
      <InnerStripeForm {...innerProps} onCardElementReady={onCardElementReady} />
    </Elements>
  );
};

export default StripeCardElementsForm;
